import { AutocompleteWithLabel } from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery
} from '@apollo/client'
import { Button, Paper } from '@material-ui/core'
import { FC, useEffect, useState } from 'react'

import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import {
  getDefaultCurrency,
  getEntityCode,
  getEntityId,
  getEntityName
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { Channel } from '../../types/channel'
import { CRSInventoryData, GetInventoryInput } from '../../types/inventory'
import { ProviderBrief } from '../../types/provider'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import useStyles from './styles/InventorySecondaryFiltersBox.styles'

interface InventorySecondaryFiltersBoxProps {
  channels: Channel[]
  loadingForChannel: boolean
  getInventoryInput: GetInventoryInput
  setgetInventoryInput: React.Dispatch<React.SetStateAction<GetInventoryInput>>
  getCRSInventory: LazyQueryExecFunction<
    {
      crsInventory: CRSInventoryData
    },
    OperationVariables
  >
  loadingForCRSInventory: boolean
  propertyCode: String
  isRoleChannel: boolean | undefined
  isRoleProvider: boolean | undefined
}

const InventorySecondaryFiltersBox: FC<InventorySecondaryFiltersBoxProps> = ({
  channels,
  loadingForChannel,
  getInventoryInput,
  setgetInventoryInput,
  getCRSInventory,
  loadingForCRSInventory,
  propertyCode,
  isRoleChannel,
  isRoleProvider
}) => {
  const classes = useStyles()

  const [autoCompleteLabelRoomType, setautoCompleteLabelRoomType] =
    useState<any>(null)
  const [autoCompleteLabelRatePlan, setautoCompleteLabelRatePlan] =
    useState<any>(null)
  const [autoCompleteLabelProvider, setautoCompleteLabelProvider] =
    useState<any>(
      isRoleProvider
        ? {
            label: getEntityName(),
            value: getEntityId(),
            code: getEntityCode()
          }
        : null
    )
  const [autoCompleteLabelForChannel, setAutoCompleteLabelForChannel] =
    useState<any>(
      isRoleChannel
        ? {
            label: getEntityName(),
            value: getEntityId()
          }
        : null
    )

  const { data: providerData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true
  })
  const { data: roomtypes, loading: loadingForRoomtypes } = useQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY, {
    variables: {
      propertyCode: propertyCode,
      showDeletedRoomTypes: false,
      showDeactivedRoomTypes: false
    }
  })
  const [getRatePlans, { data: rateplans, loading: loadingForRatePlans }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    getRatePlans({
      variables: {
        propertyCode: propertyCode,
        roomTypeCode: !!getInventoryInput.roomTypeCode
          ? getInventoryInput.roomTypeCode
          : '',
        showDeletedRatePlans: false,
        showDeactivedRatePlans: false
      }
    })
  }, [getInventoryInput.roomTypeCode])

  return (
    <div className={classes.root}>
      <Paper elevation={1} className={classes.paper}>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            label={getTranslate('Room Type')}
            placeholder={getTranslate('Select Room type')}
            includeInputInList
            onChange={(event, v: any) => {
              if (v) {
                setautoCompleteLabelRoomType({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })

                setgetInventoryInput({
                  ...getInventoryInput,
                  ratePlanCode: '',
                  roomTypeCode: v.code
                })
              } else {
                setautoCompleteLabelRoomType(null)
                setgetInventoryInput({
                  ...getInventoryInput,
                  ratePlanCode: '',
                  roomTypeCode: ''
                })
              }
              setautoCompleteLabelRatePlan(null)
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            value={autoCompleteLabelRoomType}
            options={
              roomtypes?.roomTypes?.map((roomtype) => ({
                label: `${roomtype.name} (${roomtype.displayRoomTypeCode})`,
                value: roomtype.id || '',
                code: roomtype.roomTypeCode
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForRoomtypes}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            label={getTranslate('Rateplan')}
            placeholder={getTranslate('Select Rateplan')}
            includeInputInList
            onChange={(event, v: any) => {
              if (v) {
                setautoCompleteLabelRatePlan({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setgetInventoryInput({
                  ...getInventoryInput,
                  ratePlanCode: v.code
                })
              } else {
                setautoCompleteLabelRatePlan(null)
                setgetInventoryInput({
                  ...getInventoryInput,
                  ratePlanCode: ''
                })
              }
            }}
            value={autoCompleteLabelRatePlan}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            options={
              rateplans?.ratePlans.map((rateplan) => ({
                label:
                  rateplan.name + ' (' + rateplan.displayRatePlanCode + ')',
                value: rateplan.id || '',
                code: rateplan.ratePlanCode
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            size="small"
            inputSize="small"
            loading={loadingForRatePlans}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div className={classes.formItem}>
          <AutocompleteWithLabel
            label={getTranslate('Provider')}
            placeholder={getTranslate('Select Provider')}
            includeInputInList
            onChange={(event, v: any) => {
              if (v) {
                setautoCompleteLabelProvider({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setgetInventoryInput({
                  ...getInventoryInput,
                  providerCode: v.code
                })
              } else {
                setautoCompleteLabelProvider(null)
                setgetInventoryInput({
                  ...getInventoryInput,
                  providerCode: ''
                })
              }
            }}
            getOptionSelected={(option, value) =>
              option.value === value.value && option.label === value.label
            }
            getOptionLabel={(option) => option.label || ''}
            value={autoCompleteLabelProvider}
            options={
              providerData?.providers?.map((provider) => ({
                label: `${provider.name} - (${provider.displayProviderCode})`,
                value: provider.id,
                code: provider.providerCode
              })) || []
            }
            renderOption={(option) => <>{option.label}</>}
            disabled={isRoleProvider}
            size="small"
            inputSize="small"
            loading={loadingForProvider}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        {
          <div className={classes.formItem}>
            <AutocompleteWithLabel
              label={getTranslate('Channel')}
              placeholder={getTranslate('Select Channel')}
              value={autoCompleteLabelForChannel}
              includeInputInList
              onChange={(_, v: any) => {
                if (v) {
                  setAutoCompleteLabelForChannel({
                    value: v.value,
                    label: v.label,
                    code: v.code
                  })
                  setgetInventoryInput({
                    ...getInventoryInput,
                    channelID: v.value
                  })
                } else {
                  setAutoCompleteLabelForChannel(null)
                  setgetInventoryInput({
                    ...getInventoryInput,
                    channelID: ''
                  })
                }
              }}
              getOptionSelected={(option, value) =>
                option.value === value.value && option.label === value.label
              }
              getOptionLabel={(option) => option.label || ''}
              options={
                channels
                  ? channels?.map((item) => ({
                      label: item.basicDetails.name,
                      value: item.basicDetails.channelID || '',
                      code: item.basicDetails.displayChannelCode
                    }))
                  : []
              }
              renderOption={(option) => <>{option.label}</>}
              disabled={isRoleChannel}
              size="small"
              inputSize="small"
              loading={loadingForChannel}
              loadingText={getTranslate('Please wait...')}
            />
          </div>
        }
        <div />
        <div className={classes.ButtonsContainer}>
          <Button
            variant="text"
            color="secondary"
            onClick={() => {
              if (!isRoleChannel) {
                setAutoCompleteLabelForChannel(null)
              }
              if (!isRoleProvider) {
                setautoCompleteLabelProvider(null)
              }
              setautoCompleteLabelRatePlan(null)
              setautoCompleteLabelRoomType(null)
              setgetInventoryInput({
                ...getInventoryInput,
                channelID: isRoleChannel ? getInventoryInput.channelID : '',
                Currency: getDefaultCurrency(),
                providerCode: isRoleProvider
                  ? getInventoryInput.providerCode
                  : '',
                ratePlanCode: '',
                roomTypeCode: ''
              })
            }}>
            {getTranslate('Reset')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={loadingForCRSInventory}
            onClick={() => {
              getCRSInventory({
                variables: {
                  input: getInventoryInput
                }
              })
            }}>
            {getTranslate('Apply')}
          </Button>
        </div>
      </Paper>
    </div>
  )
}

export default InventorySecondaryFiltersBox
